import { Component } from '@theme/component';

/**
 * Zerbay footer accordion (FTR-08). Below 960px each link column is a closed accordion whose heading is a toggle
 * button; from 960px every list is open and clicks are ignored. Crossing 960px resets every column.
 * The server prints the buttons with aria-expanded="false"; the inline script in sections/zerbay-footer.liquid
 * hides the lists before first paint on narrow screens, and #reset() here takes over (idempotent with it).
 *
 * Markup contract: <zerbay-footer-accordion> contains .zb-ft__col blocks, each with a button.zb-ft__toggle
 * (on:click="/toggle", aria-controls = id of its list) and a ul.zb-ft__list. The root is the closest .zb-ft.
 */
class ZerbayFooterAccordion extends Component {
  /** @type {MediaQueryList | undefined} */
  #media;

  #onChange = () => this.#reset();

  connectedCallback() {
    super.connectedCallback();

    this.#media = window.matchMedia('(min-width: 960px)');
    this.#media.addEventListener('change', this.#onChange);
    this.#reset();

    const root = this.closest('.zb-ft');
    if (root) root.classList.add('is-js');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#media) this.#media.removeEventListener('change', this.#onChange);
    this.#media = undefined;
  }

  /**
   * Flips one column (from on:click="/toggle"). Ignored at 960px and up.
   * @param {Event} event
   */
  toggle(event) {
    if (this.#media && this.#media.matches) return;

    const button = event.target instanceof Element ? event.target.closest('.zb-ft__toggle') : null;
    if (!button) return;

    const list = this.#listFor(button);
    const open = button.getAttribute('aria-expanded') !== 'true';

    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (list) list.hidden = !open;
  }

  /** Below 960px every column closed; from 960px every list open (aria-expanded stays "false", as today). */
  #reset() {
    const wide = this.#media ? this.#media.matches : false;

    for (const button of this.querySelectorAll('.zb-ft__toggle')) {
      const list = this.#listFor(button);

      button.setAttribute('aria-expanded', 'false');
      if (list) list.hidden = !wide;
    }
  }

  /**
   * @param {Element} button
   * @returns {HTMLElement | null}
   */
  #listFor(button) {
    const id = button.getAttribute('aria-controls');
    if (!id) return null;

    const list = document.getElementById(id);
    return list instanceof HTMLElement ? list : null;
  }
}

if (!customElements.get('zerbay-footer-accordion')) {
  customElements.define('zerbay-footer-accordion', ZerbayFooterAccordion);
}
